import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { Task } from '../../../types'

interface TeamMember {
  user_uuid: string
  name?: string
  role?: string
}

interface EnsureMonthlyTasksResponse {
  tasks: Task[]
  created: boolean
  message: string
}

/**
 * Hook to ensure monthly tasks exist for an organization
 *
 * This hook calls an idempotent backend endpoint that:
 * 1. Checks if monthly tasks exist for the current month
 * 2. If not, creates them automatically
 * 3. Returns all tasks (existing or newly created)
 *
 * Safe to call multiple times - won't create duplicate tasks
 */
export const useEnsureMonthlyTasks = (
  orgId: string | null,
  teamDetails: TeamMember[] | null,
  options: { enabled?: boolean } = {}
) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['ensureMonthlyTasks', orgId],
    queryFn: async (): Promise<EnsureMonthlyTasksResponse> => {
      if (!orgId) {
        return { tasks: [], created: false, message: 'No orgId provided' }
      }

      const { data } = await axios.post<EnsureMonthlyTasksResponse>(`${baseUrl}/aws/tasks/mtb/ensure-monthly-tasks`, {
        orgId,
        teamDetails: teamDetails || [],
      })

      return data
    },
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 1, // Only retry once since this creates data
    refetchOnWindowFocus: false, // Don't refetch on focus - tasks are stable
  })
}
